import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import MainLayout from "../components/mainLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { showError } from "../utils/toast";
import "../sass/pages/contratoClientePanel.scss";

const LIMITE_POR_PAGINA = 50;

const statusLabel = (status) =>
  ({
    AGUARDANDO_ASSINATURA: "Aguardando assinatura",
    VISUALIZADO: "Visualizado",
    ASSINADO: "Assinado",
    CANCELADO: "Cancelado",
    SUBSTITUIDO: "Substituído",
    REVISAO_NECESSARIA: "Revisão necessária",
    ERRO_GERACAO: "Erro na geração",
    GERADO: "Gerado",
  })[status] ||
  status ||
  "-";

export default function GestaoContratosPage() {
  const [dash, setDash] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // "termo" é o que está sendo digitado.
  // "termoAplicado" só muda quando o usuário pesquisa,
  // evitando consulta à API a cada tecla digitada.
  const [termo, setTermo] = useState("");
  const [termoAplicado, setTermoAplicado] = useState("");

  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [detalhe, setDetalhe] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);

    try {
      const [dashboardResponse, listaResponse] = await Promise.all([
        api.get("/contratos/dashboard"),
        api.get("/contratos", {
          params: {
            termo: termoAplicado,
            status,
            page,
            limit: LIMITE_POR_PAGINA,
          },
        }),
      ]);

      setDash(dashboardResponse.data || {});
      setData(listaResponse.data?.data || []);
      setTotal(Number(listaResponse.data?.total || 0));
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          "Erro ao carregar gestão de contratos.",
      );
    } finally {
      setLoading(false);
    }
  }, [page, status, termoAplicado]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const paginas = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(total / LIMITE_POR_PAGINA),
      ),
    [total],
  );

  const pesquisar = useCallback(() => {
    const novoTermo = String(termo || "").trim();

    // Se o filtro já está aplicado e estamos na primeira página,
    // executa novamente para permitir atualização manual.
    if (page === 1 && termoAplicado === novoTermo) {
      carregar();
      return;
    }

    setTermoAplicado(novoTermo);
    setPage(1);
  }, [carregar, page, termo, termoAplicado]);

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
    setPage(1);
  };

  const abrir = async (id) => {
    try {
      const { data: detalheContrato } = await api.get(
        `/contratos/${id}`,
      );

      setDetalhe(detalheContrato);
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          "Erro ao abrir contrato.",
      );
    }
  };

  return (
    <MainLayout>
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1>Gestão de Contratos</h1>
            <p>
              Acompanhamento de envios, assinaturas e
              atualizações anuais.
            </p>
          </div>
        </div>

        <div className="contrato-kpis">
          <div>
            <span>Aguardando assinatura</span>
            <strong>
              {Number(dash.aguardandoAssinatura || 0)}
            </strong>
          </div>

          <div>
            <span>Assinados</span>
            <strong>
              {Number(dash.assinados || 0)}
            </strong>
          </div>

          <div>
            <span>Revisão necessária</span>
            <strong>
              {Number(dash.revisaoNecessaria || 0)}
            </strong>
          </div>

          <div>
            <span>Erros</span>
            <strong>
              {Number(dash.erros || 0)}
            </strong>
          </div>

          <div>
            <span>Atualizações em 30 dias</span>
            <strong>
              {Number(dash.atualizacoes30Dias || 0)}
            </strong>
          </div>

          <div>
            <span>Clientes sem contrato</span>
            <strong>
              {Number(dash.semContrato || 0)}
            </strong>
          </div>
        </div>

        <section className="card section">
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <Input
              value={termo}
              onChange={(event) =>
                setTermo(event.target.value)
              }
              placeholder="Cliente, licença ou CNPJ"
              style={{ maxWidth: 360 }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  pesquisar();
                }
              }}
            />

            <select
              className="input native"
              value={status}
              onChange={handleStatusChange}
            >
              <option value="">
                Todos os status
              </option>
              <option value="AGUARDANDO_ASSINATURA">
                Aguardando assinatura
              </option>
              <option value="VISUALIZADO">
                Visualizado
              </option>
              <option value="ASSINADO">
                Assinado
              </option>
              <option value="REVISAO_NECESSARIA">
                Revisão necessária
              </option>
              <option value="ERRO_GERACAO">
                Erro
              </option>
              <option value="CANCELADO">
                Cancelado
              </option>
            </select>

            <Button
              type="button"
              onClick={pesquisar}
            >
              Pesquisar
            </Button>
          </div>

          {loading ? (
            <p>Carregando...</p>
          ) : (
            <div className="contrato-table-wrap">
              <table className="table-sm contrato-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Licença</th>
                    <th>Versão</th>
                    <th>Origem</th>
                    <th>Envio</th>
                    <th>Assinatura</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>

                <tbody>
                  {data.map((contrato) => (
                    <tr key={contrato.ID_CONTRATO}>
                      <td>
                        <strong>
                          {contrato.NomeFant ||
                            contrato.RazaoSocial ||
                            "-"}
                        </strong>
                        <br />
                        <small>
                          {contrato.RazaoSocial}
                        </small>
                      </td>

                      <td>{contrato.CODIGO_CAD}</td>

                      <td>
                        {contrato.VERSAO_TEMPLATE} /{" "}
                        {contrato.NUMERO_VERSAO}
                      </td>

                      <td>
                        {contrato.TIPO_GERACAO ===
                        "ATUALIZACAO_ANUAL"
                          ? "Automático"
                          : "Manual"}
                      </td>

                      <td>
                        {contrato.ENVIADO_EM
                          ? new Date(
                              contrato.ENVIADO_EM,
                            ).toLocaleString("pt-BR")
                          : "-"}
                      </td>

                      <td>
                        {contrato.ASSINADO_EM
                          ? new Date(
                              contrato.ASSINADO_EM,
                            ).toLocaleString("pt-BR")
                          : "-"}
                      </td>

                      <td>
                        <span
                          className={`contrato-status ${String(
                            contrato.STATUS || "",
                          ).toLowerCase()}`}
                        >
                          {statusLabel(
                            contrato.STATUS,
                          )}
                        </span>

                        {contrato.MOTIVO_STATUS && (
                          <>
                            <br />
                            <small>
                              {
                                contrato.MOTIVO_STATUS
                              }
                            </small>
                          </>
                        )}
                      </td>

                      <td>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            abrir(
                              contrato.ID_CONTRATO,
                            )
                          }
                        >
                          Rastrear
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
            }}
          >
            <span>{total} registro(s)</span>

            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <Button
                variant="secondary"
                disabled={page <= 1}
                onClick={() =>
                  setPage(
                    (paginaAtual) =>
                      paginaAtual - 1,
                  )
                }
              >
                Anterior
              </Button>

              <span>
                {page}/{paginas}
              </span>

              <Button
                variant="secondary"
                disabled={page >= paginas}
                onClick={() =>
                  setPage(
                    (paginaAtual) =>
                      paginaAtual + 1,
                  )
                }
              >
                Próxima
              </Button>
            </div>
          </div>
        </section>
      </div>

      {detalhe && (
        <div
          className="contrato-modal-backdrop"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setDetalhe(null);
            }
          }}
        >
          <section className="contrato-modal contrato-modal-small">
            <header className="contrato-modal-header">
              <div>
                <small>Rastreamento</small>
                <h2>
                  Contrato #
                  {
                    detalhe.contrato
                      ?.NUMERO_VERSAO
                  }
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  setDetalhe(null)
                }
                aria-label="Fechar rastreamento"
              >
                ×
              </button>
            </header>

            <div className="contrato-modal-body">
              <div className="contrato-timeline">
                {(detalhe.eventos || []).map(
                  (evento) => (
                    <div
                      key={evento.ID_EVENTO}
                    >
                      <time>
                        {new Date(
                          evento.CRIADO_EM,
                        ).toLocaleString(
                          "pt-BR",
                        )}
                      </time>

                      <strong>
                        {
                          evento.TIPO_EVENTO
                        }
                      </strong>

                      <p>
                        {evento.DESCRICAO}
                      </p>
                    </div>
                  ),
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </MainLayout>
  );
}
